import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Text,
  VStack,
  Container,
  Stack,
  Spinner,
  Flex,
  Badge,
  HStack,
  Divider
} from '@chakra-ui/react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'
import { useEffect, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { ko } from 'date-fns/locale'
import { getRealtimeList } from '../services/apiService'
import BigNumber from 'bignumber.js'
import CheckAuth from '../components/layouts/CheckAuth'

const Realtime = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [auth] = useRecoilState(authState)

  const [positions, setPositions] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let intervalId: NodeJS.Timeout

    const fetchData = async () => {
      try {
        const res = await getRealtimeList()
        setPositions(res.data.data.data)
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    }

    fetchData() // 최초 1회 실행

    // 10초마다 반복
    intervalId = setInterval(fetchData, 10000)

    return () => {
      clearInterval(intervalId) // 언마운트 시 해제
    }
  }, [])

  return (
    <DefaultLayout>
      <CheckAuth />
      <Container maxW="container.xl" p={0} mt={4}>
        <Box px={8} py={4}>
          {/* 제목 및 설명 */}
          <VStack align="start" spacing={1} mb={6}>
            <Text fontSize="2xl" fontWeight="bold" color="white">
              {t('실시간 포지션')}
            </Text>
            <Text fontSize="md" color="gray.400">
              {t(
                '현재 활성화된 사용자들의 실시간 포지션 정보를 확인할 수 있습니다. 최신 포지션 데이터는 자동으로 갱신됩니다.'
              )}
            </Text>
          </VStack>

          {loading ? (
            <Flex justify="center" align="center" minH="50vh">
                <Spinner color="brand.500" size="xl" />
            </Flex>
          ) : positions.length === 0 ? (
            <Box
              minH="60vh"
              display="flex"
              justifyContent="center"
              alignItems="center"
              bg="dark.card"
              borderRadius="xl"
              border="1px solid"
              borderColor="whiteAlpha.100"
            >
              <Text textAlign="center" color="gray.500">
                {t('현재 실시간 포지션 정보가 없습니다.')}
              </Text>
            </Box>
          ) : (
            <Stack spacing={4}>
              {positions.map((pos) => (
                <Box
                  key={pos.id}
                  border="1px solid"
                  borderColor="whiteAlpha.100"
                  borderRadius="xl"
                  p={6}
                  bg="dark.card"
                  backdropFilter="blur(10px)"
                  transition="all 0.2s"
                  _hover={{ borderColor: 'brand.500', transform: 'translateY(-2px)', boxShadow: 'lg' }}
                >
                  <Flex justify="space-between" align="center" mb={4}>
                    <HStack spacing={4}>
                      <Text fontWeight="bold" fontSize="lg" color="white">
                        {pos.account}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        UID: {pos.uid}
                      </Text>
                      <Badge colorScheme="cyan" variant="solid" fontSize="xs" px={2} py={0.5} borderRadius="full">
                        {pos.symbol || 'XRP'}
                      </Badge>
                    </HStack>
                    <Badge
                      colorScheme={
                        parseFloat(pos.pnlPercent) >= 0 ? 'green' : 'red'
                      }
                      fontSize="md"
                      px={3}
                      py={1}
                      borderRadius="md"
                      variant="subtle"
                    >
                      {parseFloat(pos.pnlPercent).toFixed(2)}%
                    </Badge>
                  </Flex>

                  <Flex direction={{ base: 'column', md: 'row' }} gap={6}>
                    <VStack align="start" spacing={2} flex={1}>
                      <HStack justify="space-between" w="full">
                          <Text fontSize="sm" color="gray.400">{t('평균단가')}</Text>
                          <Text fontSize="sm" color="white" fontWeight="medium">{pos.entryPrice}</Text>
                      </HStack>
                      <HStack justify="space-between" w="full">
                        <Text fontSize="sm" color="gray.400">{t('청산가')}</Text>
                        <Text fontSize="sm" color="orange.300">
                          {pos.liqPrice}
                        </Text>
                      </HStack>

                      <HStack justify="space-between" w="full">
                        <Text fontSize="sm" color="gray.400">{t('포지션')}</Text>
                        <Text
                          fontSize="sm"
                          fontWeight="bold"
                          color={pos.size > 0 ? 'green.400' : 'red.400'}
                        >
                          {pos.size > 0 ? 'Long' : 'Short'}
                        </Text>
                      </HStack>

                      <HStack justify="space-between" w="full">
                        <Text fontSize="sm" color="gray.400">{t('포지션 수량')}</Text>
                        <Text fontSize="sm" fontWeight={'bold'} color="white">
                          {new BigNumber(pos.size)
                            .abs()
                            .multipliedBy(10)
                            .toFormat(0)}{' '}
                          {pos.symbol || 'XRP'}
                        </Text>
                      </HStack>
                    </VStack>

                    <Divider orientation="vertical" display={{ base: 'none', md: 'block' }} h="auto" borderColor="whiteAlpha.200" />

                    <VStack align="start" spacing={2} flex={1}>
                      <HStack justify="space-between" w="full">
                        <Text fontSize="sm" color="gray.400">{t('실현손익')}</Text>
                        <Text
                          color={
                            new BigNumber(pos.realisedPnl).gte(0)
                              ? 'green.400'
                              : 'red.400'
                          }
                          fontSize="sm"
                          fontWeight="bold"
                        >
                          {new BigNumber(pos.realisedPnl).toFormat(2)} USDT
                        </Text>
                      </HStack>

                      <HStack justify="space-between" w="full">
                        <Text fontSize="sm" color="gray.400">{t('미실현손익')}</Text>
                        <Text
                          color={
                            new BigNumber(pos.unrealisedPnl).gte(0)
                              ? 'green.400'
                              : 'red.400'
                          }
                          fontSize="sm"
                          fontWeight="bold"
                        >
                          {new BigNumber(pos.unrealisedPnl).toFormat(2)} USDT
                        </Text>
                      </HStack>

                      <HStack justify="space-between" w="full">
                        <Text fontSize="sm" color="gray.400">{t('종료시손익')}</Text>
                        <Text
                          color={
                            new BigNumber(pos.calculatedPnl).gte(0)
                              ? 'green.400'
                              : 'red.400'
                          }
                          fontSize="sm"
                          fontWeight="bold"
                        >
                          {new BigNumber(pos.calculatedPnl).toFormat(2)} USDT
                        </Text>
                      </HStack>
                    </VStack>
                    
                    <Divider orientation="vertical" display={{ base: 'none', md: 'block' }} h="auto" borderColor="whiteAlpha.200" />

                    <VStack align="start" spacing={2} flex={1}>
                      <HStack justify="space-between" w="full">
                          <Text fontSize="sm" color="gray.400">{t('마진')}</Text>
                          <Text fontSize="sm" color="white">{new BigNumber(pos.initialMargin).toFormat(2)} USDT</Text>
                      </HStack>
                      <HStack justify="space-between" w="full">
                        <Text fontSize="sm" color="gray.400">{t('가치')}</Text>
                        <Text fontSize="sm" color="white">{pos.value} USDT</Text>
                      </HStack>

                      <HStack justify="space-between" w="full">
                        <Text fontSize="sm" color="gray.400">{t('상태')}</Text>
                        <Text fontSize="sm" color="gray.300">{pos.col1} [{pos.col2} | {pos.col3}]</Text>
                      </HStack>
                    </VStack>
                  </Flex>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Container>

      <Box mb={10} />
    </DefaultLayout>
  )
}

export default Realtime
